import { DetailedScore } from '~/interfaces';

/**
 * When each part of a result arrives, in milliseconds from the screen appearing. These are the song
 * flow animation's figures - the score counts up over four segments in 1.6s, then the grade, then
 * accuracy and combo, then the judgements, and last the timeline under them.
 *
 * Each reveal after the count-up takes {@link REVEAL_MS} to play, so the last of them has settled at
 * {@link RESULTS_REVEAL_MS}.
 */
export const RESULTS_TIMELINE = {
  countUp: 1_600,
  grade: 1_700,
  stats: 2_200,
  judgements: 2_600,
  graph: 3_000,
} as const;

/** How long one reveal takes to arrive once it starts. */
export const REVEAL_MS = 500;

// The timeline fades in like everything else, but also draws itself left to right, and it is the
// longer of the two that decides when the screen has settled.
export const GRAPH_SWEEP_MS = 600;

/** The point at which everything on the screen has finished arriving. */
export const RESULTS_REVEAL_MS = RESULTS_TIMELINE.graph + Math.max(REVEAL_MS, GRAPH_SWEEP_MS);

const SEGMENT_COUNT = 4;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/**
 * How far through its own segment the count-up is. The four run back to back rather than together,
 * so the score visibly steps through what was sung, then what it earned on top.
 */
const segmentShare = (elapsed: number, index: number) => {
  const each = RESULTS_TIMELINE.countUp / SEGMENT_COUNT;

  return clamp01((elapsed - index * each) / each);
};

/**
 * The score as the count-up has reached it, in the order the frame's four segments play: everything
 * that was sung, then the perfect bonus, the star bonus and the vibrato bonus.
 *
 * Golden rap counts with the notes rather than with the bonuses beside it - this segment is what was
 * sung, and the three after it are what it earned on top.
 */
export const countUpScore = (detailed: DetailedScore, elapsed: number) => {
  const sung = detailed.normal + detailed.rap + detailed.rapstar + detailed.freestyle;

  return (
    sung * segmentShare(elapsed, 0) +
    detailed.perfect * segmentShare(elapsed, 1) +
    detailed.star * segmentShare(elapsed, 2) +
    detailed.vibrato * segmentShare(elapsed, 3)
  );
};

export interface ResultsReveal {
  grade: boolean;
  stats: boolean;
  judgements: boolean;
  graph: boolean;
}

/** What has begun arriving by now. Each one is a single transition, never a loop. */
export const revealedAt = (elapsed: number): ResultsReveal => ({
  grade: elapsed >= RESULTS_TIMELINE.grade,
  stats: elapsed >= RESULTS_TIMELINE.stats,
  judgements: elapsed >= RESULTS_TIMELINE.judgements,
  graph: elapsed >= RESULTS_TIMELINE.graph,
});

/**
 * Whether the score has finished counting. Past this the primary stops being "skip the animation"
 * and becomes the button that moves on, which is what the frame's own timeline does at 1.6s.
 */
export const isCountUpDone = (elapsed: number) => elapsed >= RESULTS_TIMELINE.countUp;
